/**
 * Block List Expander Web Component
 *
 * Handles expand/collapse of block lists under stone type product cards on
 * collection pages. Fetches block data from the FindMyMarble API on first
 * expand and renders block items with links to the product detail page.
 *
 * Dependencies:
 *   - jQuery (slideDown/slideUp animation)
 *   - window.FindMyMarbleAPI (getBlocks method)
 *   - assets/section-block-navigation.css (styling)
 *   - snippets/skeleton-block-list.liquid (pre-rendered skeleton)
 *
 * @see snippets/block-list-item.liquid for canonical HTML reference
 */
(function () {
  'use strict';

  /* -----------------------------------------------------------------------
   * Constants
   * ----------------------------------------------------------------------- */

  var SELECTORS = {
    toggle: '.block-nav__toggle',
    content: '.block-nav__content'
  };

  var CSS_CLASSES = {
    active: 'active'
  };

  var ANIMATION_DURATION = 300;

  var ARIA = {
    expanded: 'aria-expanded'
  };

  var MESSAGES = {
    empty: 'No blocks currently available for this stone type.',
    error: 'Unable to load inventory. Please contact us.',
    apiMissing: 'Inventory data is not available at this time.'
  };

  /* -----------------------------------------------------------------------
   * HTML Helpers
   * ----------------------------------------------------------------------- */

  /**
   * Escapes HTML special characters to prevent XSS.
   *
   * @param {string} str - Raw string value from API
   * @returns {string} Escaped string safe for innerHTML
   */
  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  /**
   * Builds the thumbnail portion of a block item.
   *
   * @param {Object} block - Block object from API
   * @returns {string} HTML string for the thumbnail
   */
  function buildThumbHtml(block) {
    var src = (block.images && block.images.length > 0) ? block.images[0] : '';
    if (src) {
      return (
        '<img src="' + escapeHtml(src) + '"' +
        ' alt="' + escapeHtml(block.block_code) + ' - ' + escapeHtml(block.stone_name) + '"' +
        ' loading="lazy" width="60" height="60">'
      );
    }
    return '<div class="block-nav__item-placeholder" aria-hidden="true"></div>';
  }

  /**
   * Builds a metadata line (quality grade and origin) for a block item.
   *
   * @param {Object} block - Block object from API
   * @returns {string} HTML string for the meta span, or empty string
   */
  function buildMetaHtml(block) {
    var parts = [];
    if (block.quality_grade) parts.push(escapeHtml(block.quality_grade));
    if (block.origin_country) parts.push(escapeHtml(block.origin_country));
    if (parts.length === 0) return '';
    return '<span class="block-nav__item-meta">' + parts.join(' &middot; ') + '</span>';
  }

  /**
   * Builds a dimension line for a block item.
   *
   * @param {Object} block - Block object from API
   * @returns {string} HTML string for the dimension span, or empty string
   */
  function buildDimensionHtml(block) {
    if (!block.average_length_cm || !block.average_height_cm) return '';
    return (
      '<span class="block-nav__item-meta">' +
      block.average_length_cm + ' &times; ' + block.average_height_cm + ' cm' +
      '</span>'
    );
  }

  /**
   * Builds the right-arrow SVG icon for a block item.
   *
   * @returns {string} HTML string for the arrow icon
   */
  function buildArrowHtml() {
    return (
      '<span class="block-nav__item-arrow" aria-hidden="true">' +
        '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>' +
      '</span>'
    );
  }

  /**
   * Renders a single block list item as an HTML string.
   *
   * @param {Object} block - Block object from FindMyMarbleAPI.getBlocks()
   * @param {string} productUrl - Shopify product page URL
   * @returns {string} HTML string for one <li> block item
   */
  function buildBlockItem(block, productUrl) {
    return (
      '<li class="block-nav__item" data-block-id="' + block.id + '" data-block-code="' + escapeHtml(block.block_code) + '">' +
        '<a href="' + escapeHtml(productUrl) + '#block-' + block.id + '" class="block-nav__item-link">' +
          '<div class="block-nav__item-thumb">' + buildThumbHtml(block) + '</div>' +
          '<div class="block-nav__item-info">' +
            '<span class="block-nav__item-code">' + escapeHtml(block.block_code) + '</span>' +
            buildMetaHtml(block) +
            buildDimensionHtml(block) +
          '</div>' +
          buildArrowHtml() +
        '</a>' +
      '</li>'
    );
  }

  /**
   * Renders the full block list HTML from an array of block objects.
   *
   * @param {Array} blocks - Array of block objects from the API
   * @param {string} productUrl - Shopify product page URL
   * @returns {string} HTML string for the <ul> or empty-state message
   */
  function renderBlockList(blocks, productUrl) {
    if (!blocks || blocks.length === 0) {
      return '<p class="block-nav__empty">' + MESSAGES.empty + '</p>';
    }
    var items = blocks.map(function (block) {
      return buildBlockItem(block, productUrl);
    });
    return '<ul class="block-nav__list" role="list">' + items.join('') + '</ul>';
  }

  /**
   * Renders an inline error message.
   *
   * @returns {string} HTML string for the error display
   */
  function renderError() {
    return '<p class="block-nav__error">' + MESSAGES.error + '</p>';
  }

  /* -----------------------------------------------------------------------
   * Web Component
   * ----------------------------------------------------------------------- */

  /**
   * <block-list-expander> Web Component.
   *
   * Attributes:
   *   data-stone-name    - Stone type name for the API query
   *   data-product-url   - Product page URL for block item links
   *   data-auto-expand   - "true" to auto-expand on connectedCallback
   *
   * Expected inner markup (rendered server-side):
   *   <button class="block-nav__toggle">...</button>
   *   <div class="block-nav__content" role="region" hidden>
   *     <!-- skeleton-block-list.liquid content -->
   *   </div>
   */
  function BlockListExpander() {
    return HTMLElement.call(this) || this;
  }

  BlockListExpander.prototype = Object.create(HTMLElement.prototype);
  BlockListExpander.prototype.constructor = BlockListExpander;

  BlockListExpander.prototype.connectedCallback = function () {
    this._toggleBtn = this.querySelector(SELECTORS.toggle);
    this._content = this.querySelector(SELECTORS.content);
    this._loaded = false;
    this._loading = false;

    if (!this._toggleBtn || !this._content) return;

    this._bindEvents();
    this._setAriaControls();

    if (this.dataset.autoExpand === 'true') {
      this.expand();
    }
  };

  BlockListExpander.prototype.disconnectedCallback = function () {
    if (this._toggleBtn && this._handleToggle) {
      this._toggleBtn.removeEventListener('click', this._handleToggle);
    }
  };

  /**
   * Binds the toggle click handler.
   */
  BlockListExpander.prototype._bindEvents = function () {
    this._handleToggle = this.toggle.bind(this);
    this._toggleBtn.addEventListener('click', this._handleToggle);
  };

  /**
   * Sets up aria-controls relationship between toggle and content.
   */
  BlockListExpander.prototype._setAriaControls = function () {
    if (!this._content.id) {
      this._content.id = 'block-nav-content-' + (this.dataset.stoneName || '').replace(/\s+/g, '-').toLowerCase() + '-' + Date.now();
    }
    this._toggleBtn.setAttribute('aria-controls', this._content.id);
  };

  /**
   * Toggles the expanded/collapsed state.
   */
  BlockListExpander.prototype.toggle = function () {
    if (this.classList.contains(CSS_CLASSES.active)) {
      this.collapse();
    } else {
      this.expand();
    }
  };

  /**
   * Expands the block list content with a slide-down animation.
   * Triggers block loading if not yet loaded.
   */
  BlockListExpander.prototype.expand = function () {
    this.classList.add(CSS_CLASSES.active);
    this._toggleBtn.setAttribute(ARIA.expanded, 'true');
    this._content.removeAttribute('hidden');
    $(this._content).stop().slideDown(ANIMATION_DURATION);

    if (!this._loaded && !this._loading) {
      this.loadBlocks();
    }
  };

  /**
   * Collapses the block list content with a slide-up animation.
   */
  BlockListExpander.prototype.collapse = function () {
    var self = this;
    this.classList.remove(CSS_CLASSES.active);
    this._toggleBtn.setAttribute(ARIA.expanded, 'false');
    $(this._content).stop().slideUp(ANIMATION_DURATION, function () {
      self._content.setAttribute('hidden', '');
    });
  };

  /**
   * Fetches block data from the FindMyMarble API and renders results.
   * Guards against concurrent calls and missing API.
   */
  BlockListExpander.prototype.loadBlocks = function () {
    if (this._loaded || this._loading) return;
    this._loading = true;

    var self = this;
    var stoneName = this.dataset.stoneName;
    var productUrl = this._getProductUrl();

    if (!window.FindMyMarbleAPI) {
      self._loading = false;
      self._loaded = true;
      self._content.innerHTML = '<p class="block-nav__error">' + MESSAGES.apiMissing + '</p>';
      return;
    }

    window.FindMyMarbleAPI.getBlocks(stoneName)
      .then(function (result) {
        self._loading = false;
        self._loaded = true;

        if (result && result.error) {
          self._content.innerHTML = renderError();
          return;
        }

        var blocks = (result && result.blocks) ? result.blocks : [];
        self._content.innerHTML = renderBlockList(blocks, productUrl);
      })
      .catch(function () {
        self._loading = false;
        self._loaded = true;
        self._content.innerHTML = renderError();
      });
  };

  /**
   * Returns the product URL from the data attribute.
   *
   * @returns {string} Product page URL
   */
  BlockListExpander.prototype._getProductUrl = function () {
    return this.dataset.productUrl || '';
  };

  /* -----------------------------------------------------------------------
   * Registration
   * ----------------------------------------------------------------------- */

  if (!customElements.get('block-list-expander')) {
    customElements.define('block-list-expander', BlockListExpander);
  }
})();
